import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { StatusKind } from '../../labels';
import { StatusBadge } from '../ui';
import { Icon } from '../icons';
import { actionLabel } from '../operation/actionLabels';

/**
 * Sequences (CO-88, audit SV1, D2). A deployment and a setup run are both
 * "a run of steps with a status and logs", so they share `StepList` and
 * `LogPanel`. A to-do list is a `Checklist`; the position in a wizard is a
 * `Stepper`. One compact row per step (about 40 px), status on the right,
 * details on demand — never a bordered card per step.
 */

export interface StepItem {
  key: string;
  /** The number shown when the row is not at its list position (a folded run keeps its stage numbers). */
  number?: number;
  title: ReactNode;
  /** One line under the title. */
  description?: ReactNode;
  /** Omitted for a step that has not run (a plan's preview). */
  status?: { kind: StatusKind; value: string; label?: string };
  /** Duration or time, right of the title. */
  meta?: ReactNode;
  /** Shown under the row; long details are behind a disclosure. */
  error?: ReactNode;
  details?: ReactNode;
  /** Expanded on first render (e.g. the failed step). */
  open?: boolean;
  /** The step a summary pointed at (CO-150): opened, brought into view,
   * focused and marked, so the destination of "View error" is clear. */
  target?: boolean;
  testId?: string;
}

export function StepList({ steps, label, numbered = true }: { steps: StepItem[]; label: string; numbered?: boolean }) {
  return (
    <ol className={`step-list${numbered ? '' : ' step-list--plain'}`} aria-label={label}>
      {steps.map((step, index) => (
        <StepRow key={step.key} step={step} index={index} numbered={numbered} />
      ))}
    </ol>
  );
}

export function StepRow({ step, index, numbered }: { step: StepItem; index: number; numbered: boolean }) {
  const [open, setOpen] = useState(Boolean(step.open || step.target));
  const detailsId = useId();
  const ref = useRef<HTMLLIElement>(null);
  // Opened later (a summary's "View error"), it opens without a remount.
  useEffect(() => {
    if (step.open) setOpen(true);
  }, [step.open]);
  useEffect(() => {
    if (!step.target) return;
    setOpen(true);
    const element = ref.current;
    if (!element) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView?.({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    element.focus({ preventScroll: true });
  }, [step.target]);
  return (
    <li
      ref={ref}
      className={`step-list__item${step.target ? ' step-list__item--target' : ''}`}
      tabIndex={step.target ? -1 : undefined}
      aria-current={step.target ? 'step' : undefined}
      data-testid={step.testId}
      data-status={step.status ? `${step.status.kind}:${step.status.value}` : undefined}
    >
      <div className="step-list__row">
        {numbered ? <span className="step-list__index tabular-nums">{step.number ?? index + 1}</span> : null}
        <div className="step-list__text">
          <span className="step-list__title">{step.title}</span>
          {step.description ? <span className="step-list__description">{step.description}</span> : null}
        </div>
        {step.meta ? <span className="step-list__meta">{step.meta}</span> : null}
        {step.status ? <StatusBadge kind={step.status.kind} value={step.status.value} label={step.status.label} /> : null}
        {step.details ? (
          <button
            type="button"
            className="icon-button icon-button--ghost icon-button--sm step-list__toggle"
            aria-expanded={open}
            // The details are rendered only while open; a reference to an
            // element that is not there is an error (CO-184, DS5).
            aria-controls={open ? detailsId : undefined}
            aria-label={open ? actionLabel.hideDetails : actionLabel.viewDetails}
            title={open ? actionLabel.hideDetails : actionLabel.viewDetails}
            onClick={() => setOpen((value) => !value)}
          >
            <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
          </button>
        ) : null}
      </div>
      {step.error ? <div className="step-list__error">{step.error}</div> : null}
      {step.details && open ? (
        <div id={detailsId} className="step-list__details">
          {step.details}
        </div>
      ) : null}
    </li>
  );
}

/** The log of a run: see LogViewer (CO-150), which this name points at. */
export { LogViewer as LogPanel } from './LogViewer';
export type { LogLine } from './LogViewer';

export type ChecklistState = 'done' | 'todo' | 'not_needed';

export interface ChecklistItem {
  key: string;
  label: ReactNode;
  state: ChecklistState;
  /** Why a step is not needed, or what is left. */
  note?: ReactNode;
  /** The one action that moves this step forward (secondary, small). */
  action?: ReactNode;
}

const checklistWord: Record<ChecklistState, string> = { done: 'done', todo: 'not done yet', not_needed: 'not needed' };

/** Checklist: what has to be true, ticked as it becomes true. */
export function Checklist({ items, label, testId }: { items: ChecklistItem[]; label: string; testId?: string }) {
  return (
    <ol className="setup-steps" aria-label={label} data-testid={testId}>
      {items.map((item) => (
        <li key={item.key} className={`setup-steps__item setup-steps__item--${item.state}`}>
          <span className="setup-steps__mark" aria-hidden="true">
            {item.state === 'done' ? <Icon name="check" size={12} /> : item.state === 'not_needed' ? <Icon name="minus" size={12} /> : null}
          </span>
          <span className="setup-steps__label">
            {item.label}
            {item.note ? <span className="muted"> — {item.note}</span> : null}
            <span className="visually-hidden"> — {checklistWord[item.state]}</span>
          </span>
          {item.action ? <span className="setup-steps__action">{item.action}</span> : null}
        </li>
      ))}
    </ol>
  );
}

/** Stepper: where a wizard is. Done steps are ticked, and — when
 * `onSelect` is given — are buttons that go back to them; the current one is
 * announced with aria-current. Vertical beside a wizard on wide screens. */
export function Stepper({
  steps,
  current,
  label = 'Progress',
  orientation = 'horizontal',
  onSelect,
}: {
  steps: { key: string; label: ReactNode }[];
  current: number;
  label?: string;
  orientation?: 'horizontal' | 'vertical';
  onSelect?: (index: number) => void;
}) {
  return (
    <ol className={`stepper stepper--${orientation}`} aria-label={label}>
      {steps.map((step, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'todo';
        const content = (
          <>
            <span className="stepper__mark" aria-hidden="true">
              {state === 'done' ? <Icon name="check" size={12} /> : index + 1}
            </span>
            <span className="stepper__label">{step.label}</span>
            {state === 'done' ? <span className="visually-hidden"> — done</span> : null}
          </>
        );
        return (
          <li key={step.key} className={`stepper__step stepper__step--${state}`} aria-current={state === 'current' ? 'step' : undefined}>
            {state === 'done' && onSelect ? (
              <button type="button" className="stepper__button" onClick={() => onSelect(index)}>
                {content}
              </button>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ol>
  );
}
